import React from "react";
import { Link } from "react-router-dom";
import { Check, ArrowRight, Star } from "lucide-react";
import SectionHeading from "@/components/SectionHeading";
import Reveal from "@/components/Reveal";
import CTABanner from "@/components/CTABanner";
import Chevron from "@/components/Chevron";
import { useT } from "@/i18n/LanguageProvider";

const ENROLLMENT = { starter: 50, business: 75, professional: 100, enterprise: 125, elite: 150 };
const PRICE = { starter: 99, business: 199, professional: 349, enterprise: 549, elite: 799 };
const HIGHLIGHT_ID = "professional";

export default function Pricing() {
  const t = useT();
  const plans = (t.pricing.plans || []).map((p) => ({
    ...p,
    price: PRICE[p.id],
    enrollment: ENROLLMENT[p.id],
    highlight: p.id === HIGHLIGHT_ID,
  }));

  return (
    <>
      <section className="relative bg-delfi-dark text-white pt-36 pb-24 overflow-hidden" data-testid="pricing-hero">
        <div className="chevron-bg right-[-8%] top-[-18%] opacity-[0.08]" aria-hidden="true">›</div>
        <div className="absolute inset-0 grid-pattern opacity-40" aria-hidden="true" />
        <div className="delfi-container relative max-w-3xl">
          <div className="inline-flex items-center gap-2 text-delfi-red text-xs tracking-[0.2em] uppercase font-semibold">
            <Chevron size={14} /> {t.pricing.eyebrow}
          </div>
          <h1 className="headline-display text-white mt-4 text-[clamp(2.2rem,5vw,4rem)]" data-testid="pricing-title">
            {t.pricing.titleLead} <span className="text-delfi-red">{t.pricing.titleHighlight}</span>
          </h1>
          <p className="mt-6 text-white/75 text-lg leading-relaxed max-w-2xl">{t.pricing.description}</p>
          <div className="mt-8 flex flex-wrap gap-3 text-sm">
            {(t.pricing.badges || []).map((b, i) => (
              <span key={i} className="px-3 py-1.5 rounded-full bg-white/10 border border-white/15 text-white/80">
                {b}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Pay As You Go — for individuals / occasional senders */}
      <section className="bg-delfi-cloud py-20" data-testid="pricing-payg-section">
        <div className="delfi-container max-w-5xl">
          <Reveal>
            <SectionHeading
              eyebrow={t.pricing.payAsYouGo.eyebrow}
              title={t.pricing.payAsYouGo.title}
              description={t.pricing.payAsYouGo.description}
            />
          </Reveal>
          <Reveal delay={120}>
            <div className="mt-10 rounded-2xl bg-white shadow-[0_30px_80px_-30px_rgba(46,46,46,0.18)] border border-delfi-navy/5 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead className="bg-delfi-cloud/60 border-b border-delfi-navy/10">
                    <tr>
                      {Object.values(t.pricing.payAsYouGo.headers).map((h) => (
                        <th
                          key={h}
                          className="px-5 py-4 text-[11px] uppercase tracking-[0.18em] text-delfi-navy/70 font-bold"
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(t.pricing.payAsYouGo.rows || []).map((r, i) => (
                      <tr key={i} className="border-b border-delfi-navy/5 last:border-0">
                        <td className="px-5 py-4 font-semibold text-delfi-navy">{r.weight}</td>
                        <td className="px-5 py-4 text-delfi-navy/70 text-sm">{r.desc}</td>
                        <td className="px-5 py-4 text-delfi-navy/60 text-sm line-through">{r.market}</td>
                        <td className="px-5 py-4 font-bold text-delfi-red">{r.prepaid}</td>
                        <td className="px-5 py-4">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-delfi-gold/15 text-delfi-deep text-xs font-bold">
                            {r.save}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="px-6 py-5 bg-delfi-cloud/40 border-t border-delfi-navy/5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <p className="text-xs text-delfi-navy/60 leading-relaxed max-w-xl">
                  {t.pricing.payAsYouGo.footnote}
                </p>
                <Link
                  to="/contact"
                  className="btn-delfi-primary text-sm shrink-0"
                  data-testid="payg-cta"
                >
                  {t.pricing.payAsYouGo.ctaLabel} <ArrowRight size={16} />
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-white py-24" data-testid="pricing-plans-section">
        <div className="delfi-container">
          <Reveal>
            <SectionHeading
              eyebrow={t.pricing.section1Eyebrow}
              title={t.pricing.section1Title}
              description={t.pricing.section1Description}
            />
          </Reveal>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {plans.map((plan, i) => (
              <Reveal key={plan.id} delay={i * 80}>
                <div
                  className={`relative h-full rounded-2xl p-7 flex flex-col ${
                    plan.highlight
                      ? "bg-delfi-navy text-white shadow-[0_30px_60px_-20px_rgba(46,46,46,0.45)] scale-[1.02]"
                      : "bg-white delfi-card"
                  }`}
                  data-testid={`pricing-card-${plan.id}`}
                >
                  {plan.highlight && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-delfi-red text-white text-xs font-bold tracking-wider uppercase px-3 py-1.5 rounded-full shadow-lg flex items-center gap-1.5">
                      <Star size={12} fill="white" /> {t.pricing.mostPopular}
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <Chevron size={14} />
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-delfi-red">{plan.name}</p>
                  </div>
                  <div className="mt-5 flex items-baseline gap-1">
                    <span className={`text-5xl font-extrabold tracking-tight ${plan.highlight ? "text-white" : "text-delfi-navy"}`}>
                      OMR {plan.price}
                    </span>
                    <span className={`text-sm ${plan.highlight ? "text-white/60" : "text-delfi-navy/60"}`}>
                      {t.pricing.monthly}
                    </span>
                  </div>
                  <p className={`mt-2 text-xs ${plan.highlight ? "text-white/55" : "text-delfi-navy/55"}`}>
                    {t.pricing.enrollment}: OMR {plan.enrollment} · {t.pricing.effective} {plan.perDelivery} {t.pricing.perDelivery}
                  </p>
                  <p className={`mt-4 font-semibold ${plan.highlight ? "text-white" : "text-delfi-navy"}`}>{plan.deliveries}</p>
                  <p className={`mt-2 text-sm ${plan.highlight ? "text-white/70" : "text-delfi-navy/65"}`}>{plan.tagline}</p>
                  <ul className="mt-6 space-y-2.5 flex-1">
                    {plan.features.map((f, j) => (
                      <li key={j} className="flex items-start gap-2.5">
                        <span
                          className={`mt-0.5 w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${
                            plan.highlight ? "bg-delfi-red/20 text-delfi-red" : "bg-delfi-red/10 text-delfi-red"
                          }`}
                        >
                          <Check size={12} strokeWidth={3} />
                        </span>
                        <span className={`text-sm leading-relaxed ${plan.highlight ? "text-white/85" : "text-delfi-navy/80"}`}>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/partner"
                    className={`mt-7 ${plan.highlight ? "btn-delfi-primary" : "btn-delfi-secondary"} w-full justify-center`}
                    data-testid={`pricing-cta-${plan.id}`}
                  >
                    {t.pricing.startWith} {plan.name} <ArrowRight size={16} />
                  </Link>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Rate card */}
      <section className="bg-delfi-cloud py-24" data-testid="pricing-rate-card-section">
        <div className="delfi-container">
          <Reveal>
            <SectionHeading
              eyebrow={t.pricing.rateCard.eyebrow}
              title={t.pricing.rateCard.title}
              description={t.pricing.rateCard.description}
            />
          </Reveal>
          <Reveal delay={120}>
            <div className="mt-12 overflow-hidden rounded-2xl bg-white border border-delfi-navy/10">
              <div className="grid grid-cols-1 md:grid-cols-5 text-xs uppercase tracking-[0.18em] font-semibold bg-delfi-navy text-white">
                <div className="p-5 text-white/80">{t.pricing.rateCard.headers.weight}</div>
                <div className="p-5 text-white/80 hidden md:block">{t.pricing.rateCard.headers.description}</div>
                <div className="p-5 text-white/80">{t.pricing.rateCard.headers.market}</div>
                <div className="p-5 text-white/80">{t.pricing.rateCard.headers.prepaid}</div>
                <div className="p-5 text-white/80">{t.pricing.rateCard.headers.save}</div>
              </div>
              {(t.pricing.rateCard.rows || []).map((row, idx) => (
                <div
                  key={idx}
                  className={`grid grid-cols-1 md:grid-cols-5 text-sm ${idx % 2 === 0 ? "bg-white" : "bg-delfi-cloud/60"}`}
                >
                  <div className="p-5 font-semibold text-delfi-navy flex items-center gap-2">
                    <Chevron size={12} /> {row.weight}
                  </div>
                  <div className="p-5 text-delfi-navy/70 hidden md:block">{row.desc}</div>
                  <div className="p-5 text-delfi-navy/70 line-through decoration-delfi-red/60">{row.market}</div>
                  <div className="p-5 text-delfi-navy font-bold">{row.prepaid}</div>
                  <div className="p-5">
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-delfi-red/10 text-delfi-red text-xs font-bold">
                      −{row.save}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
          <p className="mt-6 text-sm text-delfi-navy/60 text-center">{t.pricing.rateCard.footer}</p>
        </div>
      </section>

      {/* Billing */}
      <section className="bg-white py-24" data-testid="pricing-billing-section">
        <div className="delfi-container grid grid-cols-1 md:grid-cols-2 gap-8">
          <Reveal>
            <div className="delfi-card h-full">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-delfi-red flex items-center gap-2">
                <Chevron size={12} /> {t.pricing.billing.prepaidEyebrow}
              </p>
              <h3 className="mt-3 text-2xl font-bold text-delfi-navy">{t.pricing.billing.prepaidTitle}</h3>
              <ul className="mt-5 space-y-2.5 text-delfi-navy/75 text-sm">
                {t.pricing.billing.prepaidItems.map((it, i) => (
                  <li key={i}>· {it}</li>
                ))}
              </ul>
            </div>
          </Reveal>
          <Reveal delay={120}>
            <div className="delfi-card h-full">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-delfi-red flex items-center gap-2">
                <Chevron size={12} /> {t.pricing.billing.postpaidEyebrow}
              </p>
              <h3 className="mt-3 text-2xl font-bold text-delfi-navy">{t.pricing.billing.postpaidTitle}</h3>
              <ul className="mt-5 space-y-2.5 text-delfi-navy/75 text-sm">
                {t.pricing.billing.postpaidItems.map((it, i) => (
                  <li key={i}>· {it}</li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      <CTABanner
        title={t.pricing.ctaTitle}
        subtitle={t.pricing.ctaSubtitle}
        primaryLabel={t.common.startPilot}
      />
    </>
  );
}
